import { MapPin, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { RootState } from '~/redux/store';
import {
  useGetOrganizationLocationsPage,
  type OrganizationLocationsFilters,
} from '~/api/facility';
import {
  useAssignCurrentUserToLocation,
  useUnassignUserFromLocation,
} from '~/api/location';
import {
  LocationScope,
  type GetOrganizationLocationsPageQuery,
} from '~/graphql/graphql';
import { Button } from '~/components/ui';
import { RetryBanner } from '~/components/ui/RetryBanner';
import { usePaginatedQuery } from '~/hooks/usePaginatedQuery';
import { useDebouncedSearchInput } from '~/hooks/useDebouncedSearchInput';
import type { PaginatedResponse } from '~/types';
import type { AddressLike } from '~/utils/addressFormat';
import { groupLocationsByBranch } from '~/utils/locationBranch';
import { FacilityCardSkeleton } from './FacilityCardSkeleton';
import { ListToolbar } from './ListToolbar';
import { LocationRow } from './LocationRow';
import { UpsertLocationModal } from './UpsertLocationModal';
import { DeleteLocationDialog } from './DeleteLocationDialog';

export type LocationRowData = {
  id: string;
  code: string;
  name: string;
  branchCode?: string | null;
  facility: {
    id: string;
    code?: string | null;
    address: AddressLike;
  };
};

type PagedLocation = NonNullable<
  GetOrganizationLocationsPageQuery['organizationLocationsPage']['content'][number]
>;

const PAGE_SIZE = 20;

export function OrganizationLocations() {
  const { t } = useTranslation('settings');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingLocation, setEditingLocation] =
    useState<LocationRowData | null>(null);
  const [deletingLocation, setDeletingLocation] =
    useState<LocationRowData | null>(null);
  const [scope, setScope] = useState<LocationScope>(LocationScope.All);
  const { searchInputValue, activeSearch, handleSearchChange } =
    useDebouncedSearchInput();
  const debouncedSearch = activeSearch.trim();

  const assignUser = useAssignCurrentUserToLocation();
  const unassignUser = useUnassignUserFromLocation();

  const queryParams = useMemo<OrganizationLocationsFilters>(
    () => ({ search: debouncedSearch || undefined, scope }),
    [debouncedSearch, scope]
  );

  const {
    data: rows,
    sentinelRef,
    isLoading,
    isLoadingMore,
    hasMoreData,
    removeItem,
    reset,
    error,
    refetch,
  } = usePaginatedQuery<
    GetOrganizationLocationsPageQuery,
    PagedLocation,
    OrganizationLocationsFilters
  >({
    fetchHook: useGetOrganizationLocationsPage,
    select: (data) =>
      data?.organizationLocationsPage as PaginatedResponse<PagedLocation>,
    options: { pageSize: PAGE_SIZE, filters: queryParams },
  });

  const userLocations = useSelector(
    (state: RootState) => state.auth.user?.locations
  );
  const assignedLocationIds = useMemo(
    () => new Set((userLocations ?? []).map((location) => location.id)),
    [userLocations]
  );

  const groups = useMemo(() => groupLocationsByBranch(rows), [rows]);
  const noResults = !isLoading && rows.length === 0;

  const emptyStateMessage = () => {
    if (scope === LocationScope.Mine) {
      return debouncedSearch === ''
        ? t('settings.locations.no_assigned_locations')
        : t('settings.locations.no_results');
    }
    return debouncedSearch === ''
      ? t('settings.locations.no_locations')
      : t('settings.locations.no_results');
  };

  const toggleAssignment = async (
    location: LocationRowData,
    isAssigned: boolean
  ) => {
    const mutation = isAssigned ? unassignUser : assignUser;
    const action = isAssigned ? 'unassign' : 'assign';
    try {
      await mutation.mutateAsync({ locationId: location.id });
      toast.success(t(`settings.facilities.locations.${action}_success`));
    } catch {
      toast.error(t(`settings.facilities.locations.${action}_error`));
    }
  };

  return (
    <section
      className="w-full bg-neut-bg-alt rounded-lg border border-neut-200 p-6 shadow-sm"
      data-testid="locations-section"
    >
      <div className="flex justify-between items-start gap-4 mb-6">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-prim-100 rounded-lg">
            <MapPin size={20} className="text-prim" />
          </div>
          <div>
            <h2
              className="text-neut-prim font-semibold"
              data-testid="locations-heading"
            >
              {t('settings.locations.title')}
            </h2>
            <p className="text-sm text-neut-sec mt-1">
              {t('settings.locations.description')}
            </p>
          </div>
        </div>
        <Button
          data-testid="add-location-button"
          onClick={() => setShowCreateModal(true)}
          width="auto"
          size="sm"
        >
          <Plus size={16} /> {t('settings.locations.add_button')}
        </Button>
      </div>

      <ListToolbar
        searchTerm={searchInputValue}
        onSearchChange={handleSearchChange}
        searchPlaceholder={t('settings.locations.search_placeholder')}
        searchTestId="location-search"
        scope={{
          value: scope,
          onChange: setScope,
          options: [
            {
              value: LocationScope.All,
              label: t('settings.locations.filter_all'),
              testId: 'location-filter-all',
            },
            {
              value: LocationScope.Mine,
              label: t('settings.locations.filter_my_locations'),
              testId: 'location-filter-mine',
            },
          ],
        }}
      />

      {isLoading ? (
        <FacilityCardSkeleton />
      ) : error ? (
        <RetryBanner
          message={t('settings.locations.load_failed')}
          retryLabel={t('settings.locations.retry')}
          onRetry={() => refetch()}
          testId="locations"
        />
      ) : noResults ? (
        <p
          className="text-sm text-neut-sec text-center py-6"
          data-testid="locations-empty-state"
        >
          {emptyStateMessage()}
        </p>
      ) : (
        <>
          <div className="space-y-4" data-testid="locations-list">
            {groups.map((group) => (
              <div
                key={group.branchCode ?? '__no_branch__'}
                data-testid="branch-group"
              >
                <div
                  className="border-b border-neut-200 pb-1 mb-2"
                  data-testid="branch-group-header"
                >
                  <h3 className="text-sm font-semibold text-neut-prim">
                    {group.branchCode
                      ? t('settings.locations.branch_heading', {
                          branchCode: group.branchCode,
                        })
                      : t('settings.locations.no_branch_code')}
                  </h3>
                </div>
                <div className="space-y-1">
                  {group.locations.map((location) => {
                    const isAssigned = assignedLocationIds.has(location.id);
                    return (
                      <LocationRow
                        key={location.id}
                        location={location}
                        isAssigned={isAssigned}
                        onToggleAssignment={() =>
                          toggleAssignment(location, isAssigned)
                        }
                        onEdit={() => setEditingLocation(location)}
                        onDelete={() => setDeletingLocation(location)}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          {isLoadingMore && hasMoreData && <FacilityCardSkeleton />}
          <div
            ref={sentinelRef}
            data-testid="locations-scroll-sentinel"
            style={{ height: '1px' }}
          />
        </>
      )}

      {showCreateModal && (
        <UpsertLocationModal
          isOpen
          onClose={() => setShowCreateModal(false)}
          onSaved={reset}
        />
      )}

      {editingLocation && (
        <UpsertLocationModal
          isOpen
          onClose={() => setEditingLocation(null)}
          onSaved={reset}
          location={editingLocation}
        />
      )}

      {deletingLocation && (
        <DeleteLocationDialog
          isOpen
          onClose={() => setDeletingLocation(null)}
          onDeleted={removeItem}
          location={deletingLocation}
        />
      )}
    </section>
  );
}
